import { lazy } from "react";
import { Navigate } from "react-router";
import { RequirePermission } from "../components/RequirePermission";
import { Route } from "react-router";

const AgentsGraphPage = lazy(() =>
  import("../features/ai/graph/AgentsGraphPage").then((m) => ({ default: m.AgentsGraphPage })),
);
const StyleExperimentsPage = lazy(() =>
  import("../pages/ai/style-experiments").then((m) => ({ default: m.StyleExperimentsPage })),
);
const AgentEditorPage = lazy(() =>
  import("../features/ai/agents/AgentEditorPage").then((m) => ({ default: m.AgentEditorPage })),
);
const NewAgentWizardPage = lazy(() =>
  import("../features/ai/agents/wizard/NewAgentWizardPage").then((m) => ({
    default: m.NewAgentWizardPage,
  })),
);
const AiSetupWizardPage = lazy(() =>
  import("../features/ai/setup/AiSetupWizardPage").then((m) => ({ default: m.AiSetupWizardPage })),
);
const AiTestConsolePage = lazy(() =>
  import("../pages/ai/test-console").then((m) => ({ default: m.AiTestConsolePage })),
);
const KbFixQueuePage = lazy(() =>
  import("../features/ai/kb-fix-queue/QueueList").then((m) => ({ default: m.QueueList })),
);
const FaqCandidates = lazy(() =>
  import("../features/ai/faq-mining/candidates").then((m) => ({ default: m.FaqCandidates })),
);
const FaqMiningReport = lazy(() =>
  import("../features/ai/faq-mining/report").then((m) => ({ default: m.FaqMiningReport })),
);
const AiHubPage = lazy(() =>
  import("../features/ai/hub/AiHubPage").then((m) => ({ default: m.AiHubPage })),
);
const AiHubRedirect = lazy(() =>
  import("../features/ai/hub/AiHubRedirect").then((m) => ({ default: m.AiHubRedirect })),
);
const SystemRepliesPage = lazy(() =>
  import("../features/automation/SystemRepliesPage").then((m) => ({
    default: m.SystemRepliesPage,
  })),
);
const FlowEditorRedirect = lazy(() =>
  import("../features/flows/FlowEditorRoute").then((m) => ({ default: m.FlowEditorRedirect })),
);
const FormBuilderPage = lazy(() =>
  import("../features/forms/builder").then((m) => ({ default: m.FormBuilderPage })),
);
const FormResponsesPage = lazy(() =>
  import("../features/forms/FormResponsesPage").then((m) => ({ default: m.FormResponsesPage })),
);
const FormsPage = lazy(() =>
  import("../features/forms/FormsPage").then((m) => ({ default: m.FormsPage })),
);
const TemplateEditorPage = lazy(() =>
  import("../features/templates/editor/TemplateEditor").then((m) => ({
    default: m.TemplateEditorPage,
  })),
);
const TemplatesPage = lazy(() =>
  import("../features/templates/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
);
const MediaGalleryPage = lazy(() =>
  import("../features/media/MediaGalleryPage").then((m) => ({ default: m.MediaGalleryPage })),
);
const BroadcastReportPage = lazy(() =>
  import("../features/broadcasts/BroadcastReportPage").then((m) => ({
    default: m.BroadcastReportPage,
  })),
);
const BroadcastsPage = lazy(() =>
  import("../features/broadcasts/BroadcastsPage").then((m) => ({ default: m.BroadcastsPage })),
);
// B29-06: the workspace-wide source library (websites, sheets, bulk upload) is its own page again;
// each agent picks its own information in its «المعلومات» tab.
const KnowledgePage = lazy(() =>
  import("../features/knowledge/KnowledgePage").then((m) => ({ default: m.KnowledgePage })),
);

const BroadcastComposer = lazy(() =>
  import("../features/broadcasts/composer/BroadcastComposer").then((m) => ({
    default: m.BroadcastComposer,
  })),
);

// Route order inside <Route path="/w/:workspaceId"> is preserved: this fragment slots in where it was inline.
export const workspaceAiRoutes = (
  <>
    <Route path="ai" element={<AiHubPage />} />
    <Route path="knowledge" element={<KnowledgePage />} />
    <Route path="ai/kb-fix-queue" element={<KbFixQueuePage />} />
    <Route path="ai/faq-mining" element={<FaqCandidates />} />
    <Route
      path="ai/weekly-report"
      element={<Navigate replace to="../../reports/weekly" relative="path" />}
    />
    <Route path="ai/faq-mining/report" element={<FaqMiningReport />} />
    <Route path="ai-setup" element={<AiSetupWizardPage />} />
    <Route path="ai-agents" element={<AiHubRedirect section="agent" />} />
    <Route path="ai-agents/graph" element={<AgentsGraphPage />} />
    <Route path="ai-agents/new" element={<NewAgentWizardPage />} />
    <Route path="ai-agents/test-console" element={<AiTestConsolePage />} />
    <Route path="ai-agents/:agentId" element={<AgentEditorPage />} />
    <Route path="ai-agents/:agentId/experiments" element={<StyleExperimentsPage />} />
    <Route path="system-replies" element={<SystemRepliesPage />} />
    <Route path="campaigns" element={<Navigate to="../broadcasts" replace />} />
    <Route path="inbox" element={<Navigate to="../conversations" replace />} />
    <Route path="flows/:flowId" element={<FlowEditorRedirect />} />
    <Route path="forms" element={<FormsPage />} />
    <Route path="forms/:formId" element={<FormBuilderPage />} />
    <Route path="forms/:formId/responses" element={<FormResponsesPage />} />
    <Route
      path="broadcasts"
      element={
        <RequirePermission anyOf={["broadcasts.view"]} reasonKey="broadcasts.forbidden">
          <BroadcastsPage />
        </RequirePermission>
      }
    />
    <Route
      path="broadcasts/new"
      element={
        <RequirePermission anyOf={["broadcasts.view"]} reasonKey="broadcasts.forbidden">
          <BroadcastComposer />
        </RequirePermission>
      }
    />
    <Route
      path="broadcasts/:broadcastId"
      element={
        <RequirePermission anyOf={["broadcasts.view"]} reasonKey="broadcasts.forbidden">
          <BroadcastReportPage />
        </RequirePermission>
      }
    />
    <Route
      path="media"
      element={
        <RequirePermission anyOf={["flows.view"]}>
          <MediaGalleryPage />
        </RequirePermission>
      }
    />
    <Route path="templates" element={<TemplatesPage />} />
    <Route path="templates/new" element={<TemplateEditorPage />} />
    <Route path="templates/:templateId" element={<TemplateEditorPage />} />
  </>
);
